// Primitivos de formulário do domínio Client.
// Seguem DESIGN_SYSTEM §componentes/Inputs:
//  - altura 40px, border-radius 10px
//  - border-line / focus border-line-2
//  - erro: borda --bad + texto erro em red-300/80
//  - mensagem específica, nunca "campo inválido"

import { forwardRef, useId } from 'react';
import type {
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react';

import { cn } from '@control-center/ui';

interface FieldProps {
  label: string;
  hint?: string;
  error?: string;
  required?: boolean;
  className?: string;
  children: (ids: { inputId: string; describedBy: string | undefined }) => ReactNode;
}

export function Field({
  label,
  hint,
  error,
  required,
  className,
  children,
}: FieldProps): JSX.Element {
  const reactId = useId();
  const inputId = `cc-field-${reactId}`;
  const describedBy = hint || error ? `${inputId}-desc` : undefined;

  return (
    <div className={cn('flex flex-col gap-[6px]', className)}>
      <label
        htmlFor={inputId}
        className="text-[11px] font-medium uppercase tracking-wider text-muted-3"
      >
        {label}
        {required && <span className="ml-[2px] text-bad">*</span>}
      </label>
      {children({ inputId, describedBy })}
      {(hint || error) && (
        <div
          id={describedBy}
          className={cn('text-[11px] leading-[1.4]', error ? 'text-red-300/80' : 'text-muted-2')}
        >
          {error ?? hint}
        </div>
      )}
    </div>
  );
}

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  hasError?: boolean;
  tabular?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, hasError, tabular, ...rest },
  ref,
) {
  return (
    <input
      ref={ref}
      className={cn(
        'h-10 w-full rounded-[10px] border bg-panel px-3 text-[13px] text-ink',
        'placeholder:text-muted-3 outline-none transition-colors',
        'focus:border-line-2',
        hasError ? 'border-bad/70 focus:border-bad' : 'border-line',
        tabular && 'tnum',
        className,
      )}
      aria-invalid={hasError || undefined}
      {...rest}
    />
  );
});

interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  hasError?: boolean;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { className, hasError, children, ...rest },
  ref,
) {
  return (
    <select
      ref={ref}
      className={cn(
        'h-10 w-full appearance-none rounded-[10px] border bg-panel px-3 pr-9 text-[13px] text-ink',
        'outline-none transition-colors focus:border-line-2',
        hasError ? 'border-bad/70 focus:border-bad' : 'border-line',
        // chevron via background-image
        "bg-[url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%2212%22 height=%2212%22 viewBox=%220 0 24 24%22 fill=%22none%22 stroke=%22%2371717a%22 stroke-width=%222%22 stroke-linecap=%22round%22 stroke-linejoin=%22round%22><polyline points=%226 9 12 15 18 9%22/></svg>')] bg-[length:12px_12px] bg-[right_12px_center] bg-no-repeat",
        className,
      )}
      aria-invalid={hasError || undefined}
      {...rest}
    >
      {children}
    </select>
  );
});

interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  hasError?: boolean;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { className, hasError, ...rest },
  ref,
) {
  return (
    <textarea
      ref={ref}
      className={cn(
        'w-full rounded-[10px] border bg-panel px-3 py-[10px] text-[13px] text-ink',
        'placeholder:text-muted-3 outline-none transition-colors',
        'focus:border-line-2',
        hasError ? 'border-bad/70 focus:border-bad' : 'border-line',
        className,
      )}
      aria-invalid={hasError || undefined}
      {...rest}
    />
  );
});
